"use client";

import { useEffect, useRef, useState } from "react";
import { flushSync } from "react-dom";
import Image from "next/image";
import { useSearchParams } from "next/navigation";
import { useTranslation } from "react-i18next";
import { useLocalizedRouter } from "@/app/hooks/useLocalizedRouter";
import { trackAutoBuilderFormSubmit } from "@/app/lib/tracking";
import { createLogger } from "@/app/lib/logger";
import { localizedClipSrc } from "@/app/utils/localizedClip";
import { AnimatedIcon } from "../AnimatedIcon";
import { ARROW_ICON, CAMPAIGN_ICON } from "../animatedIcons";
import CompanyLinkInput from "../CompanyLinkInput";
import { Finn } from "../Finn";
import { CheckMark } from "./BusinessMarks";
import { splitTrustLine } from "./FinalSignal";
import TiltCard from "./TiltCard";

const log = createLogger("HeroSignal");

export const DEMO_BOOKING_URL =
  "https://calendly.com/yenc-achtfach/beratungsgesprach-1";

const HERO_CHANNEL_ICONS = [
  "/images/Facebook.png",
  "/images/Insta.png",
  "/images/Linkedin.png",
  "/images/Google.png",
];

const delay = (ms: number): React.CSSProperties => ({
  animationDelay: `${ms}ms`,
});

interface HeroSignalProps {
  /** Page language from the server; picks the German or English hero loop. */
  language?: string;
}

export default function HeroSignal({ language }: HeroSignalProps = {}) {
  const { t } = useTranslation();
  const router = useLocalizedRouter();
  const searchParams = useSearchParams();
  const [shouldLoadVideo, setShouldLoadVideo] = useState(false);
  const [videoLoaded, setVideoLoaded] = useState(false);
  const [videoInView, setVideoInView] = useState(true);
  const [demoOpen, setDemoOpen] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const demoRef = useRef<HTMLDialogElement>(null);
  const demoVideoRef = useRef<HTMLVideoElement>(null);

  // Defer the large MP4 until the browser is idle so the poster wins LCP.
  // It plays under reduced motion too: it is the product demo, not decoration.
  useEffect(() => {
    const loadVideo = () => setShouldLoadVideo(true);

    if (typeof window.requestIdleCallback === "function") {
      const idleId = window.requestIdleCallback(loadVideo, { timeout: 2500 });
      return () => window.cancelIdleCallback(idleId);
    }

    const timeoutId = globalThis.setTimeout(loadVideo, 1800);
    return () => globalThis.clearTimeout(timeoutId);
  }, []);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || typeof IntersectionObserver === "undefined") return;
    const observer = new IntersectionObserver(entries =>
      setVideoInView(entries.some(entry => entry.isIntersecting))
    );
    observer.observe(video);
    return () => observer.disconnect();
  }, []);

  // Play only while the shot is on screen and the full demo is closed; the
  // loop has no reason to run behind the rest of the page.
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !videoLoaded) return;
    if (videoInView && !demoOpen) {
      video.play().catch(() => {});
    } else {
      video.pause();
    }
  }, [videoLoaded, videoInView, demoOpen]);

  useEffect(() => {
    if (demoOpen) demoRef.current?.showModal();
  }, [demoOpen]);

  // Safari and iOS only allow sound when play() runs inside the click
  // itself; autoplay on a video mounted afterwards is blocked, leaving the
  // demo paused with its dimming control bar stuck on top.
  const openDemo = () => {
    flushSync(() => setDemoOpen(true));
    demoVideoRef.current?.play().catch(() => {});
  };

  const handleFormSubmit = async (companyUrl: string) => {
    sessionStorage.setItem("companyUrl", companyUrl);
    sessionStorage.setItem("hasPendingJob", "true");

    const source =
      searchParams.get("utm_source") ||
      (document.referrer ? new URL(document.referrer).hostname : "direct");

    trackAutoBuilderFormSubmit({
      company_url: companyUrl,
      trigger_source: source,
    }).catch((error: unknown) => {
      log.error("Failed to track AutoBuilder form submit", error, {
        trigger_source: source,
      });
    });

    router.push("/register");
  };

  const trustItems = splitTrustLine(t("landing.finalCta.trustLine", ""));
  const kpis = [
    {
      key: "applications",
      value: "10x",
      label: t("landing.valueTriptych.results.stats.applications.label", ""),
    },
    {
      key: "cost",
      value: "-70%",
      label: t("landing.valueTriptych.results.stats.cost.label", ""),
    },
    {
      key: "time",
      value: `14 ${t("landing.valueTriptych.results.stats.time.unit", "")}`,
      label: t("landing.valueTriptych.results.stats.time.label", ""),
    },
  ].filter(kpi => kpi.label);

  // The first two trust items double as the capsules suspended over the shot.
  const chips = trustItems.slice(0, 2);

  // The entrance fills `backwards` only: a finished animation that still
  // fills acts like will-change on opacity and becomes the backdrop root of
  // everything inside, so the URL field, chips, ghost and coins would blur
  // and refract an empty layer instead of the page.
  const rise = "animate-orbit-rise motion-reduce:animate-none";

  return (
    <section aria-labelledby="hero-title" className="relative overflow-x-clip">
      <div className="relative mx-auto w-full max-w-[1280px] px-4 pb-14 pt-24 sm:px-6 lg:px-8 lg:pb-6 lg:pt-28">
        <div className="grid grid-cols-1 items-center gap-y-14 lg:grid-cols-12 lg:gap-x-10">
          <div className="lg:col-span-6">
            <h1
              id="hero-title"
              className={`text-balance font-display text-[clamp(2.5rem,5vw,4.25rem)] font-semibold leading-[1] tracking-[-0.04em] text-signal-ink ${rise}`}
              style={delay(80)}
            >
              {t("landing.hero.headline", "Social Recruiting")}{" "}
              <span className="text-signal-accent">
                {t("landing.hero.headlineAccent", "auf Autopilot.")}
              </span>
            </h1>

            <p
              className={`mt-6 max-w-[48ch] text-pretty text-[17px] leading-[1.6] text-signal-muted ${rise}`}
              style={delay(160)}
            >
              {t("landing.hero.subheadline")}
            </p>

            <div className={rise} style={delay(240)}>
              <CompanyLinkInput
                variant="bare"
                ctaIcon={CAMPAIGN_ICON}
                onSubmit={handleFormSubmit}
                className="w-full"
              />

              {trustItems.length > 0 && (
                <ul className="mt-6 flex flex-wrap gap-2.5">
                  {trustItems.map(item => (
                    <li
                      key={item}
                      className="glass-pill flex items-center gap-2 py-2 pl-2.5 pr-4 text-[13px] font-medium text-signal-ink"
                    >
                      <CheckMark className="h-4 w-4 text-signal-success" />
                      {item}
                    </li>
                  ))}
                </ul>
              )}

              {/* A no-account path for visitors who aren't ready to sign up:
                  the demo puts a person in the loop. */}
              <div className="mt-7 flex flex-wrap items-center gap-3">
                <a
                  href={DEMO_BOOKING_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-glass-ghost group h-11 pl-5 pr-4 text-[15px] text-signal-deep"
                >
                  {t("landing.hero.bookDemo", "Demo buchen")}
                  <span
                    aria-hidden="true"
                    className="glass-arrow text-[12px] transition-transform duration-300 group-hover:translate-x-0.5"
                  >
                    <AnimatedIcon
                      icon={ARROW_ICON}
                      className="h-[1.15em] w-[1.15em]"
                    />
                  </span>
                </a>
              </div>

              {/* Figures as three lit discs, so the proof reads as objects
                  rather than as a table of numbers. The coins are lenses;
                  their figures are small, so they take the deep blue. */}
              {kpis.length > 0 && (
                <dl className="mt-10 grid grid-cols-3 gap-2 sm:flex sm:flex-wrap sm:gap-x-4 sm:gap-y-6">
                  {kpis.map(kpi => (
                    <div
                      key={kpi.key}
                      className="flex flex-col items-center text-center sm:w-[120px]"
                    >
                      <dd className="glass glass-lens flex aspect-square w-full max-w-[92px] items-center justify-center rounded-full font-signal-mono text-[17px] font-semibold sm:h-[104px] sm:w-[104px] sm:max-w-none sm:text-[19px] tabular-nums tracking-[-0.02em] text-signal-deep">
                        {kpi.value}
                      </dd>
                      <dt className="mt-3 text-[13px] leading-snug text-signal-muted">
                        {kpi.label}
                      </dt>
                    </div>
                  ))}
                </dl>
              )}
            </div>
          </div>

          {/* The product shot as a slab suspended in space, casting a soft
              shadow on the floor beneath it, with two promises floating in
              front of it. */}
          <div className={`relative lg:col-span-6 ${rise}`} style={delay(320)}>
            {/* The shadow stays flat on the page, outside the card's 3D space:
                it is the floor the card hovers over, not part of the card. */}
            <span
              aria-hidden="true"
              className="pointer-events-none absolute inset-x-[10%] -bottom-10 h-16 rounded-[50%] bg-signal-ink/25 blur-2xl"
            />

            <TiltCard
              restX={3}
              restY={-9}
              strength={5}
              lift={18}
              className="glass-flat relative rounded-glassPanel p-3 sm:p-4"
            >
              <div className="flex items-center justify-between gap-3 pb-3 pl-3 pr-1 pt-1.5">
                <span className="glass-inset rounded-full px-4 py-1.5 font-signal-mono text-[11px] text-signal-muted">
                  heyfinn.ai
                </span>
                {/* The icons are 3D tiles with their own shading, so they are
                    shown whole rather than cropped into discs. */}
                <span aria-hidden="true" className="flex items-center gap-1">
                  {HERO_CHANNEL_ICONS.map(icon => (
                    <span key={icon} className="relative h-7 w-7">
                      <Image
                        src={icon}
                        alt=""
                        fill
                        sizes="28px"
                        className="object-contain"
                      />
                    </span>
                  ))}
                </span>
              </div>

              {/* The screen: small corners under the header, and the card's
                  own curves (less the padding) where it meets the card edge. */}
              <div className="relative aspect-[16/9] overflow-hidden rounded-[28px] bg-signal-ink">
                <img
                  src={localizedClipSrc("/images/hero-poster.webp", language)}
                  alt=""
                  aria-hidden="true"
                  className="absolute inset-0 h-full w-full object-cover object-top"
                  loading="eager"
                  fetchPriority="high"
                />
                <video
                  ref={videoRef}
                  data-testid="hero-video"
                  aria-hidden="true"
                  src={
                    shouldLoadVideo
                      ? localizedClipSrc(
                          "/videos/landing/heyfinn-intro.mp4",
                          language
                        )
                      : undefined
                  }
                  poster={localizedClipSrc(
                    "/images/hero-poster.webp",
                    language
                  )}
                  className={`absolute inset-0 h-full w-full object-cover object-top transition-opacity duration-700 ${
                    videoLoaded ? "opacity-100" : "opacity-0"
                  }`}
                  muted
                  loop
                  playsInline
                  autoPlay
                  preload="none"
                  onLoadedData={() => setVideoLoaded(true)}
                />
                {/* The loop is silent; a click opens the full cut with sound.
                    The pill sits on the video, so it is the strong sheet. */}
                <button
                  type="button"
                  data-testid="hero-demo-open"
                  onClick={openDemo}
                  className="group/play absolute inset-0 flex items-end justify-start p-3 focus-visible:outline-none sm:p-4"
                >
                  <span className="glass-strong flex items-center gap-2.5 rounded-full py-1.5 pl-1.5 pr-4 text-[13px] font-semibold text-signal-ink transition-transform duration-300 ease-[cubic-bezier(0.16,1,0.3,1)] group-hover/play:scale-105 group-focus-visible/play:ring-4 group-focus-visible/play:ring-signal-accent/40">
                    <span className="btn-glass-primary h-8 w-8">
                      <svg
                        aria-hidden="true"
                        viewBox="0 0 24 24"
                        className="ml-0.5 h-3.5 w-3.5 fill-current"
                      >
                        <path d="M8 5v14l11-7z" />
                      </svg>
                    </span>
                    {t("landing.hero.watchFullDemo", "Komplette Demo ansehen")}
                  </span>
                </button>
                {/* The recording is white, so without a hairline the screen
                    melts into the white card around it. */}
                <span
                  aria-hidden="true"
                  className="pointer-events-none absolute inset-0 rounded-[inherit] ring-1 ring-inset ring-signal-ink/10"
                />
              </div>

              {/* Two promises lifted out of the trust row and suspended in
                  front of the shot on opposite edges: the first over the
                  header's empty middle, the second across the bottom rim, so
                  neither covers the screen or meets the text column beside
                  the card at laptop widths. The list spans the
                  whole card: preserve-3d makes it the chips' containing block,
                  so a collapsed list would pin them below the card. */}
              {/* Finn perched on the card's top edge, parallaxed in front like
                  the chips. Mounted with the deferred loop so his render
                  never competes with the poster for LCP; from sm up only,
                  where the stacked layout leaves room above the card. */}
              {shouldLoadVideo && (
                <span className="pointer-events-none absolute bottom-full right-2 z-20 -mb-6 hidden w-32 [transform:translateZ(90px)] sm:block lg:w-40">
                  <Finn state="hello" className={rise} />
                </span>
              )}

              {/* The chips live in the tilt's 3D space, where glass cannot
                  blur: they stay flat glass and never become lenses. */}
              {chips.length > 0 && (
                <ul
                  aria-hidden="true"
                  data-testid="hero-chips"
                  className="pointer-events-none absolute inset-0 [transform-style:preserve-3d]"
                >
                  {chips.map((chip, index) => (
                    <li
                      key={chip}
                      className={`glass-flat absolute z-10 hidden items-center gap-3 rounded-full py-2.5 pl-2.5 pr-5 sm:flex ${
                        index === 0
                          ? "-top-6 left-[30%] [transform:translateZ(70px)]"
                          : "-bottom-6 right-10 [transform:translateZ(50px)]"
                      }`}
                    >
                      <span className="glass-check h-9 w-9">
                        <CheckMark className="h-5 w-5" />
                      </span>
                      <span className="whitespace-nowrap text-[14px] font-semibold text-signal-ink">
                        {chip}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </TiltCard>
          </div>
        </div>
      </div>

      {/* Full demo with sound. The video only mounts while open, so the
          larger file loads on demand and the audio stops on close. */}
      <dialog
        ref={demoRef}
        data-testid="hero-demo"
        aria-label={t("landing.hero.demoTitle", "heyFinn - Komplette Demo")}
        onClose={() => setDemoOpen(false)}
        onClick={event => {
          if (event.target === event.currentTarget) demoRef.current?.close();
        }}
        className="m-auto w-[min(92vw,1100px)] max-w-none overflow-visible bg-transparent p-0 backdrop:bg-signal-ink/80 backdrop:backdrop-blur-sm"
      >
        {demoOpen && (
          <div className="relative">
            {/* Over the dimmed backdrop the close control is the strong
                sheet. Not btn-glass-ghost: on one element its clear fill
                would win over glass-strong. */}
            <button
              type="button"
              onClick={() => demoRef.current?.close()}
              aria-label={t("common.close") || "Close"}
              className="glass-strong absolute -top-12 right-0 inline-flex h-10 w-10 items-center justify-center rounded-full text-[22px] font-semibold leading-none transition-colors duration-300 hover:text-signal-deep"
            >
              <span aria-hidden="true">×</span>
            </button>
            <video
              ref={demoVideoRef}
              data-testid="hero-demo-video"
              src={localizedClipSrc(
                "/videos/landing/heyfinn-intro-full.mp4",
                language
              )}
              poster={localizedClipSrc("/images/hero-poster.webp", language)}
              className="aspect-video w-full rounded-[1.25rem] bg-signal-ink shadow-orbitFloat"
              controls
              playsInline
            />
          </div>
        )}
      </dialog>
    </section>
  );
}
